<template>
  <div>
    <h2 :class="['headline', 'font-grey', 'font-l-600']" v-html="title" />
    <div :class="['server-side-description']" v-html="description" />
    <div :class="['blurred-text']">
      <p>Once you are done installing into your <code>node_modules</code>, you need to include Vuecidity into your main
        entry file (typically <code>/src/main.js</code>):</p>
      <div :class="['mt-16']">
        <code>
          <span :class="['font-grey']">// src/main.js</span><br />
          import Vue from 'vue'<br />
          import Vuecidity from 'vuecidity'<br />
          import '../node_modules/vuecidity/dist/lib/vuecidity.min.css'<br /><br />
          Vue.use(Vuecidity)<br />
        </code>
      </div>
      <example-title title="NPM installation" />
      <div :class="['my-16']">
        <code>$ npm install vuecidity --save</code>
      </div>
      <example-title title="Yarn installation" />
      <div :class="['my-16']">
        <code>$ yarn add vuecidity</code>
      </div>
      <example-title title="jsDelivr CDN" />
      <div :class="['mt-16']">
        <code>
          <span :class="['font-grey']">&lt;!-- stylesheet --&gt;</span><br />
          &lt;link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.css" /&gt;<br />
          <span :class="['font-grey']">&lt;!-- vuejs --&gt;</span><br />
          &lt;script src="https://cdn.jsdelivr.net/npm/vue@2/dist/vue.min.js"&gt;&lt;/script&gt;<br />
          <span :class="['font-grey']">&lt;!-- vuecidity --&gt;</span><br />
          &lt;script src="https://cdn.jsdelivr.net/npm/vuecidity@{{ vuecidityVersion }}"&gt;&lt;/script&gt;<br />
          <span :class="['font-grey']">or...</span><br />
          &lt;script src="https://cdn.jsdelivr.net/npm/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.js"&gt;&lt;/script&gt;<br />
        </code>
      </div>
      <example-title title="UnPkg CDN" />
      <div :class="['mt-16']">
        <code>
          <span :class="['font-grey']">&lt;!-- stylesheet --&gt;</span><br />
          &lt;link rel="stylesheet" href="https://unpkg.com/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.css" /&gt;<br />
          <span :class="['font-grey']">&lt;!-- vuejs --&gt;</span><br />
          &lt;script src="https://unpkg.com/vue/dist/vue.js"&gt;&lt;/script&gt;<br />
          <span :class="['font-grey']">&lt;!-- vuecidity --&gt;</span><br />
          &lt;script src="https://unpkg.com/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.js"&gt;&lt;/script&gt;<br />
        </code>
      </div>
      <!--<example-title title="bundle.run CDN" />-->
      <!--<div :class="['mt-16']">-->
        <!--<code>-->
          <!--<span :class="['font-grey']">&lt;!&#45;&#45; stylesheet &#45;&#45;&gt;</span><br />-->
          <!--&lt;link rel="stylesheet" href="https://bundle.run/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.css" /&gt;<br />-->
          <!--<span :class="['font-grey']">&lt;!&#45;&#45; vuejs &#45;&#45;&gt;</span><br />-->
          <!--&lt;script src="https://cdn.jsdelivr.net/npm/vue@2/dist/vue.min.js"&gt;&lt;/script&gt;<br />-->
          <!--<span :class="['font-grey']">&lt;!&#45;&#45; vuecidity &#45;&#45;&gt;</span><br />-->
          <!--&lt;script src="https://bundle.run/vuecidity@{{ vuecidityVersion }}/dist/lib/vuecidity.min.js"&gt;&lt;/script&gt;<br />-->
        <!--</code>-->
      <!--</div>-->
      <example-title title="Including the Roboto font and the icons for VcIcon" />
      <p>The web fonts for the <a href="https://fonts.google.com/specimen/Roboto" target="_blank">Roboto</a>
        font and the icons for <router-link to="/components/icon">VcIcon</router-link> should be used from CDN as
        they are not included in package.json:</p>
      <div :class="['my-16']">
        <code>
          <span :class="['font-grey']">&lt;!-- index.html --&gt;</span><br />
          &lt;link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Roboto|Material+Icons" /&gt;<br />
          &lt;link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/MaterialDesign-Webfont/2.0.46/css/materialdesignicons.min.css" /&gt;<br />
          &lt;link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css" /&gt;<br />
          &lt;link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/weather-icons/2.0.9/css/weather-icons.min.css" /&gt;<br />
          &lt;link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/weather-icons/2.0.9/css/weather-icons-wind.min.css" /&gt;<br />
          &lt;link rel="stylesheet" href="https://code.ionicframework.com/ionicons/2.0.1/css/ionicons.min.css" /&gt;<br />
          &lt;link rel="stylesheet" href="https://afeld.github.io/emoji-css/emoji.css" /&gt;
        </code>
      </div>
      <vc-alert color="error" icon="error">
        <a href="http://govicons.io/" target="_blank">Govicons</a> are currently not served via CDN, so in
        order to use them you need to download and add them manually to your project:
      </vc-alert>
      <code>
        <span :class="['font-grey']">&lt;!-- index.html --&gt;</span><br />
        <span :class="['font-grey']">&lt;!-- /static/govicons --&gt;</span><br />
        &lt;link rel="stylesheet" href="/static/govicons/css/govicons.min.css" /&gt;
      </code>
      <example-title title="Including the SVG files for VcFlag" />
      <p>The flag icons used for <router-link to="/components/flag">VcFlag</router-link> were taken
        from Flag Icon CSS, crafted by <a href="https://lip.is/" target="_blank">Panayiotis Lipiridis</a>.
        Flag Icon CSS is included in the <code>package.json</code> of Vuecidity, but once installed, it has to be
        moved to your project's <code>/static</code> folder, so the SVG files are accessible.</p>
      <code>
        <span :class="['font-grey']">/static/flag-icon-css</span>
      </code>
    </div>
  </div>
</template>
<script>
import methods from '../helpers/methods'

export default {
  name: 'QuickStartPage',
  mixins: [
    methods
  ],
  data () {
    return {
      title: null,
      description: null,
      vuecidityVersion: this.$store.getters.vuecidityVersion
    }
  },
  watch: {
    '$store.state.vuecidityVersion' () {
      this.vuecidityVersion = this.$store.getters.vuecidityVersion
    }
  },
  mounted () {
    this.getPage()
  }
}
</script>
<style>
/*.blurred-text*/
/*{*/
  /*-webkit-filter: blur(4px);*/
  /*-moz-filter: blur(4px);*/
  /*filter: blur(4px);*/
/*}*/
</style>



// WEBPACK FOOTER //
// src/pages/quick-start.vue